<script setup>
import Card from '../templates/Card.vue';
import {
  getUrlParameter,
  cleanupUrlParameter,
} from '../utils/productCountUtils.js';
import ProductDetailModal from '../modals/ProductDetailModal.vue';
import Pagination from '../components/Pagination.vue';
import { usePagination } from '../composables/usePagination.js';
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

const detailModalDetails = ref([]);
const showProductDetailModal = ref(false);
const loading = ref(true);
const pakketten = ref([]);
const selectingPackageId = ref(null);

const { currentPage, isChangingPage, paginate, goToPage, resetPagination } =
  usePagination({
    perPage: 24,
    scrollTarget: '.step-base-selection',
  });

const props = defineProps({
  data: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
  endpoint: { type: String, required: true },
  packageType: { type: String, default: 'large' },
  mode: { type: String, default: null },
  isMailboxSession: { type: Boolean, default: false },
  flowType: { type: String, default: null },
  currentStep: { type: Number, default: 0 },
});

const emit = defineEmits([
  'update',
  'next',
  'go-to-step',
  'reset-package',
  'start',
  'increase-product',
  'decrease-product',
  'add-to-cart',
  'remove-product',
  'remove-food',
  'remove-packaging',
  'select-base',
  'printing-updated',
  'remove-base-package',
]);

const paginatedData = computed(() => paginate(pakketten.value));
const showPagination = computed(() => paginatedData.value.totalPages > 1);

const reloadHandler = (event) => {
  if (event?.detail?.packageType) {
    resetPagination();
    fetchPackagesWithType(event.detail.packageType);
  }
};

onMounted(() => {
  document.addEventListener('s7_reload_products', reloadHandler);
  fetchPackages().then(() => {
    const pakketId = getUrlParameter('pakket');
    const step = getUrlParameter('stap');
    if (pakketId && Array.isArray(pakketten.value)) {
      const match = pakketten.value.find(
        (p) => String(p.id) === String(pakketId),
      );
      if (match) {
        selectBase(match).finally(() => {
          if (step === 'personaliseren') {
            emit('go-to-step', 4);
            // Clean up stap parameter after navigation completes
            setTimeout(() => {
              cleanupUrlParameter('stap');
            }, 300);
          }
        });
      }
    }
  });
});

onBeforeUnmount(() => {
  document.removeEventListener('s7_reload_products', reloadHandler);
});

async function fetchPackages() {
  return fetchPackagesWithType(props.packageType);
}

async function fetchPackagesWithType(packageType = props.packageType) {
  try {
    const url = new URL('/api/packages', window.location.origin);
    if (packageType === 'small') {
      url.searchParams.set('mailbox', '1');
    }

    const res = await fetch(url.toString());

    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }

    const contentType = res.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Response is not JSON');
    }

    const packages = await res.json();

    let packagesArray;
    if (packages._debug) {
      delete packages._debug;
      packagesArray = Object.values(packages);
    } else {
      packagesArray = Array.isArray(packages) ? packages : [];
    }

    pakketten.value = packagesArray.filter(
      (p) => (p.price ?? p.totalPrice ?? 0) > 0 && p.title?.trim(),
    );

    loading.value = false;
    resetPagination();

    if (pakketten.value.length > 0) {
      setTimeout(async () => {
        try {
          const { preloadPackageData } = await import(
            '../utils/dataProcessor.js'
          );
          const sortedPackages = [...pakketten.value].sort(
            (a, b) =>
              (a.price || a.totalPrice || 0) - (b.price || b.totalPrice || 0),
          );
          await preloadPackageData(sortedPackages.slice(0, 5));
        } catch (preloadError) {
          console.warn('Package preloading failed:', preloadError);
        }
      }, 500);
    }
  } catch (e) {
    console.error('Fout bij laden pakketten:', e);
    loading.value = false;
  }
}

const isSelectedPackage = (pakket) => {
  return props.data.basePackage?.id === pakket.id;
};

async function selectBase(pakket) {
  if (selectingPackageId.value === pakket?.id) {
    return;
  }

  selectingPackageId.value = pakket?.id;

  event?.preventDefault?.();
  event?.stopPropagation?.();

  emit('select-base', pakket);

  selectingPackageId.value = null;
}

const closeForegroundModal = () => {
  if (detailModalDetails.value.length > 1) {
    detailModalDetails.value.pop();
  } else {
    detailModalDetails.value = [];
    showProductDetailModal.value = false;
  }
};

const openNewModal = (addedModalData, modal = null) => {
  detailModalDetails.value.push({
    ...addedModalData,
  });
  showProductDetailModal.value = detailModalDetails.value.length > 0;
};
</script>

<style scoped>
.loading-package {
  position: relative;
  overflow: hidden;
}

.loading-package::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  animation: loading-shimmer 1.5s infinite;
}

@keyframes loading-shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}
</style>

<template>
  <div class="step-base-selection">
    <div v-if="!loading" class="row g-2">
      <div
        class="col-6 col-md-4"
        v-for="pakket in paginatedData.items"
        :key="pakket.id"
      >
        <Card
          :product="{
            id: pakket.id,
            name: pakket.title,
            price: (() => {
              const price = pakket.price ?? pakket.totalPrice ?? 0;
              return price;
            })(),
            price_incl_tax: pakket.price_incl_tax ?? '',
            image: pakket.image,
            images: pakket.images,
            packaging: pakket.packaging,
            products: pakket.products,
            foodDrinks: pakket.foodDrinks,
            userPackaging: data.packaging,
            userPackagingProduct: data.packagingProduct,
            userPackagingMode: data.packagingMode,
            size: pakket.size,
          }"
          :count="0"
          :isPackage="true"
          :packageType="packageType"
          :selected="isSelectedPackage(pakket)"
          :isImageClickable="true"
          :isImageHigher="true"
          @open-modal="
            openNewModal({
              product: {
                id: pakket.id,
                name: pakket.title,
                price: (() => {
                  const price = pakket.price ?? pakket.totalPrice ?? 0;
                  return price;
                })(),
                price_incl_tax: pakket.price_incl_tax ?? '',
                image: pakket.image,
                images: pakket.images,
                packaging: pakket.packaging,
                products: pakket.products,
                foodDrinks: pakket.foodDrinks,
                userPackaging: data.packaging,
                userPackagingProduct: data.packagingProduct,
                userPackagingMode: data.packagingMode,
                size: pakket.size,
              },
              count: 0,
              isPackage: true,
              packageType: packageType,
              selected: isSelectedPackage(pakket),
              isEditable: true,
              type: 'base',
            })
          "
          @select="selectBase"
        />
      </div>
    </div>
    <div class="row g-2" v-else>
      <div class="col-6 col-md-4" v-for="n in 24" :key="n">
        <div class="skeleton skeleton--product-card"></div>
      </div>
    </div>

    <Pagination
      v-if="!loading && showPagination"
      :currentPage="currentPage"
      :totalPages="paginatedData.totalPages"
      :loading="isChangingPage"
      @page-changed="goToPage"
    />
  </div>

  <ProductDetailModal
    v-for="(modal, index) in detailModalDetails"
    v-if="showProductDetailModal"
    :zIndex="index"
    :key="index"
    :product="modal.product"
    :count="modal.count || 0"
    :type="modal.type"
    :isPackage="modal.type === 'base'"
    :isPackaging="modal.type === 'packaging'"
    :isPersonalizedCard="modal.type === 'card'"
    :selected="modal.selected !== null ? modal.selected : true"
    :isEditable="modal.isEditable !== null ? modal.isEditable : true"
    @select="selectBase"
    @close-modal="closeForegroundModal"
    @add-modal="(newModalData) => openNewModal(newModalData, modal)"
  />
</template>
